// useFieldDisabling.ts
import { computed, type Ref, unref } from 'vue';

interface UseFieldDisablingOptions<T> {
  editMode: Ref<boolean> | boolean;
  allDisabled?: Ref<boolean | undefined> | boolean;
  disabledFields?:
    | Ref<Partial<Record<keyof T, boolean>> | undefined>
    | Partial<Record<keyof T, boolean>>;
}

/**
 * Central helper to decide whether a field should be disabled.
 * - Respects allDisabled
 * - Respects disabledFields[key]
 * - Falls back to !editMode
 */
export function useFieldDisabling<T>(options: UseFieldDisablingOptions<T>) {
  const editMode = computed(() => unref(options.editMode));
  const allDisabled = computed(() => unref(options.allDisabled) ?? false);
  const disabledFields = computed(
    () => unref(options.disabledFields) ?? ({} as Partial<Record<keyof T, boolean>>),
  );

  const isDisabled = (key?: keyof T): boolean => {
    if (allDisabled.value) return true;

    if (key && disabledFields.value[key]) {
      return true;
    }

    return !editMode.value;
  };

  return { isDisabled };
}
